import {
  STAGE_LABELS,
  VIGOUR_BUBBLES,
  VIGOUR_LABELS,
  acidById,
  indicatorReading,
  partnerById,
  stageExplanation,
  stagePH,
  stagesFor,
  type AcidReaction,
  type IndicatorStage,
} from './acidReactions';
import type { IndicatorColour } from './pHScale';
import { parseFormula, stripState, type EquationTerm } from './reactionPredictionData';

/** Token approximations of universal indicator colours; every swatch also carries a text label. */
const UNIVERSAL_FILLS: Record<IndicatorColour, string> = {
  red: 'fill-brand-coral',
  orange: 'fill-brand-coral',
  yellow: 'fill-brand-sunshine',
  green: 'fill-brand-mint',
  blue: 'fill-brand-sky',
  purple: 'fill-brand-bubblegum',
};

const UNIVERSAL_SWATCHES: Record<IndicatorColour, string> = {
  red: 'bg-brand-coral',
  orange: 'bg-gradient-to-r from-brand-coral to-brand-sunshine',
  yellow: 'bg-brand-sunshine',
  green: 'bg-brand-mint',
  blue: 'bg-brand-sky',
  purple: 'bg-gradient-to-r from-brand-sky to-brand-bubblegum',
};

const LITMUS_SWATCHES: Record<string, string> = {
  Red: 'bg-brand-coral',
  'Purple (no change)': 'bg-gradient-to-r from-brand-sky to-brand-bubblegum',
  Blue: 'bg-brand-sky',
};

const PHENOLPHTHALEIN_SWATCHES: Record<string, string> = {
  Colourless: 'bg-white',
  Pink: 'bg-brand-bubblegum',
};

const BEAKER_WIDTH = 240;
const BEAKER_HEIGHT = 220;
const LIQUID_TOP = 90;
const BEAKER_LEFT = 50;
const BEAKER_RIGHT = 190;
const BEAKER_BOTTOM = 200;
const SOLID_PIECES = 6;
const BUBBLE_X_STEP = 37;
const BUBBLE_Y_STEP = 29;
const BUBBLE_X_SPAN = 120;
const BUBBLE_Y_SPAN = 90;

export function BeakerSchematic({
  reaction,
  revealed,
  stage,
}: {
  reaction: AcidReaction;
  revealed: boolean;
  stage: IndicatorStage;
}) {
  const partner = partnerById(reaction.partner);
  const reading = indicatorReading(stagePH(reaction, revealed ? stage : 'start'));
  const bubbles = revealed ? VIGOUR_BUBBLES[reaction.vigour] : 0;
  const solidRemains = !revealed || reaction.pattern === 'no-reaction' || stage === 'start';
  const showsSolid = partner.species.endsWith('(s)') && solidRemains;
  const label = `Beaker of ${acidById(reaction.acid).name} with universal indicator (${reading.universal.label.toLowerCase()}, pH about ${reading.pH})${showsSolid ? ` and ${partner.name}` : ''}. ${revealed ? VIGOUR_LABELS[reaction.vigour] : 'Not yet mixed'}.`;

  return (
    <figure className="rounded-[1.5rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4">
      <svg viewBox={`0 0 ${BEAKER_WIDTH} ${BEAKER_HEIGHT}`} className="mx-auto w-full max-w-[16rem]" role="img" aria-label={label}>
        <path
          d={`M${BEAKER_LEFT} ${LIQUID_TOP} L${BEAKER_LEFT} ${BEAKER_BOTTOM} L${BEAKER_RIGHT} ${BEAKER_BOTTOM} L${BEAKER_RIGHT} ${LIQUID_TOP} Z`}
          className={`${UNIVERSAL_FILLS[reading.universal.colour]} opacity-40`}
        />
        <path
          d={`M${BEAKER_LEFT - 6} 30 L${BEAKER_LEFT} 36 L${BEAKER_LEFT} ${BEAKER_BOTTOM} L${BEAKER_RIGHT} ${BEAKER_BOTTOM} L${BEAKER_RIGHT} 36 L${BEAKER_RIGHT + 6} 30`}
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          className="text-ink-soft"
        />
        <line x1={BEAKER_LEFT} x2={BEAKER_RIGHT} y1={LIQUID_TOP} y2={LIQUID_TOP} stroke="currentColor" className="text-slate2" strokeDasharray="4 3" />
        {showsSolid
          ? Array.from({ length: SOLID_PIECES }, (_, index) => (
              <rect
                key={index}
                x={BEAKER_LEFT + 20 + index * 17}
                y={BEAKER_BOTTOM - 14 - (index % 2) * 4}
                width="12"
                height="10"
                rx="2"
                className={partner.id === 'CuO' ? 'fill-ink' : partner.id === 'Cu' ? 'fill-brand-coral' : 'fill-stone2'}
              />
            ))
          : null}
        {Array.from({ length: bubbles }, (_, index) => (
          <circle
            key={index}
            cx={BEAKER_LEFT + 10 + ((index * BUBBLE_X_STEP) % BUBBLE_X_SPAN)}
            cy={LIQUID_TOP + 8 + ((index * BUBBLE_Y_STEP) % BUBBLE_Y_SPAN)}
            r={3 + (index % 3)}
            fill="white"
            stroke="currentColor"
            className="animate-pulse text-slate2 motion-reduce:animate-none"
          />
        ))}
      </svg>
      <figcaption className="mt-2 text-center text-xs font-bold leading-5 text-slate2">
        Schematic from the model, not to scale · {revealed ? VIGOUR_LABELS[reaction.vigour] : 'not yet mixed'} · universal
        indicator {reading.universal.label.toLowerCase()} (pH about {reading.pH})
      </figcaption>
    </figure>
  );
}

function countAtoms(terms: EquationTerm[]) {
  const totals: Record<string, number> = {};
  terms.forEach((term) =>
    Object.entries(parseFormula(stripState(term.species))).forEach(([element, count]) => {
      totals[element] = (totals[element] ?? 0) + term.coefficient * count;
    }),
  );
  return totals;
}

export function AtomCheck({ reaction }: { reaction: AcidReaction }) {
  const left = countAtoms(reaction.reactants);
  const right = countAtoms(reaction.products);
  const elements = [...new Set([...Object.keys(left), ...Object.keys(right)])];
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[16rem] text-left text-sm">
        <caption className="sr-only">Atoms on each side of the balanced equation</caption>
        <thead>
          <tr className="text-xs font-extrabold uppercase tracking-wide text-slate2">
            <th scope="col" className="py-1 pr-3">Element</th>
            <th scope="col" className="py-1 pr-3">Reactants</th>
            <th scope="col" className="py-1 pr-3">Products</th>
            <th scope="col" className="py-1">Check</th>
          </tr>
        </thead>
        <tbody>
          {elements.map((element) => (
            <tr key={element} className="border-t border-hairline font-bold text-ink-soft">
              <th scope="row" className="py-1.5 pr-3 font-extrabold text-ink">{element}</th>
              <td className="py-1.5 pr-3">{left[element] ?? 0}</td>
              <td className="py-1.5 pr-3">{right[element] ?? 0}</td>
              <td className="py-1.5">{(left[element] ?? 0) === (right[element] ?? 0) ? '✓ equal' : '✗ not equal'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IndicatorCard({ name, value, swatch, detail }: { name: string; value: string; swatch: string; detail: string }) {
  return (
    <div className="rounded-2xl border border-hairline bg-white p-3">
      <p className="text-xs font-extrabold uppercase tracking-wide text-slate2">{name}</p>
      <div className="mt-2 flex items-center gap-3">
        <span className={`h-8 w-8 shrink-0 rounded-full border border-hairline ${swatch}`} aria-hidden="true" />
        <span className="text-sm font-extrabold text-ink">{value}</span>
      </div>
      <p className="mt-1 text-xs font-medium leading-5 text-slate2">{detail}</p>
    </div>
  );
}

export function IndicatorPanel({
  reaction,
  stage,
  onStageChange,
}: {
  reaction: AcidReaction;
  stage: IndicatorStage;
  onStageChange: (stage: IndicatorStage) => void;
}) {
  const stages = stagesFor(reaction);
  const reading = indicatorReading(stagePH(reaction, stage));
  return (
    <section aria-label="Indicator change" className="rounded-[1.5rem] border border-hairline bg-surface p-4 sm:p-5">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Indicator change</p>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Choose a stage">
        {stages.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={stage === option}
            onClick={() => onStageChange(option)}
            className={`rounded-full border px-3 py-1.5 text-xs font-extrabold transition-colors motion-reduce:transition-none ${
              stage === option ? 'border-ink bg-ink text-white' : 'border-hairline bg-white text-slate2 hover:border-ink/30'
            }`}
          >
            {STAGE_LABELS[option]}
          </button>
        ))}
      </div>
      <p className="mt-3 text-sm font-medium leading-6 text-ink-soft">{stageExplanation(reaction, stage)}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <IndicatorCard
          name="Litmus"
          value={reading.litmus}
          swatch={LITMUS_SWATCHES[reading.litmus]}
          detail="Red in acid, blue in alkali."
        />
        <IndicatorCard
          name="Universal indicator"
          value={`${reading.universal.label} · pH ${reading.pH}`}
          swatch={UNIVERSAL_SWATCHES[reading.universal.colour]}
          detail="Shows roughly how acidic or alkaline."
        />
        <IndicatorCard
          name="Phenolphthalein"
          value={reading.phenolphthalein}
          swatch={PHENOLPHTHALEIN_SWATCHES[reading.phenolphthalein]}
          detail="Colourless in acid and neutral; pink in alkali (from about pH 8.2)."
        />
      </div>
    </section>
  );
}
